import React, { Component } from 'react';
import { CalendarCheckIcon, BlocksIcon, MonitorPlayIcon, TrendingUpIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
type Benefit = {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  metric: string;
  metricLabel: string;
  title: string;
  desc: string;
  bullets: string[];
  accent: 'primary' | 'green' | 'amber' | 'rose';
  featured?: boolean;
};
const benefits: Benefit[] = [{
  icon: CalendarCheckIcon,
  metric: '5',
  metricLabel: 'Volumi 1ª-5ª',
  title: 'Programmazione pronta',
  desc: 'Unità mese per mese, allineate alle Indicazioni Nazionali.',
  bullets: ['Obiettivi MIUR mappati', 'Piano annuale + settimanale', 'Pronto per la segreteria'],
  accent: 'primary'
}, {
  icon: BlocksIcon,
  metric: '100+',
  metricLabel: 'Laboratori',
  title: 'Matematica viva',
  desc: 'Giochi e attività che rendono i numeri concreti e indimenticabili.',
  bullets: ['Materiali stampabili A4', 'Lavoro in coppie e gruppi', 'Versione BES/DSA inclusa'],
  accent: 'green'
}, {
  icon: MonitorPlayIcon,
  metric: '50+',
  metricLabel: 'Slide LIM',
  title: 'Lezioni LIM pronte',
  desc: 'Spiegazioni animate di geometria e aritmetica, chiare in un attimo.',
  bullets: ['LIM, tablet e PC', 'Animazioni dei concetti chiave', 'Quiz interattivi inclusi'],
  accent: 'amber'
}, {
  icon: TrendingUpIcon,
  metric: '+40%',
  metricLabel: 'Voti in classe',
  title: "Metodo d'eccellenza",
  desc: 'Sistema testato sul campo: meno ansia, più voti in tutta la classe.',
  bullets: ["Riduce l'ansia da prestazione", 'INVALSI ready', '+40% voti misurato'],
  accent: 'rose',
  featured: true
}];
const accentMap = {
  primary: {
    iconBg: 'bg-primary/10',
    iconBorder: 'border-primary/30',
    iconColor: 'text-primary',
    metric: 'text-primary',
    border: 'hover:border-primary/60'
  },
  green: {
    iconBg: 'bg-cta-green/10',
    iconBorder: 'border-cta-green/30',
    iconColor: 'text-cta-green',
    metric: 'text-cta-green',
    border: 'hover:border-cta-green/60'
  },
  amber: {
    iconBg: 'bg-secondary/10',
    iconBorder: 'border-secondary/30',
    iconColor: 'text-secondary',
    metric: 'text-secondary',
    border: 'hover:border-secondary/60'
  },
  rose: {
    iconBg: 'bg-badge-red/10',
    iconBorder: 'border-badge-red/30',
    iconColor: 'text-badge-red',
    metric: 'text-badge-red',
    border: 'hover:border-badge-red/60'
  }
};
export function BenefitsSection() {
  return <section className="w-full bg-bg-main py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-1">
      {/* Background accents */}
      <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(234,88,12,0.08) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-2" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-3" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-4">
        {/* Section header — compact */}
        <div className="text-center mb-8 sm:mb-12" data-id="element-5">
          <span className="inline-flex items-center gap-2 text-primary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full mb-3 sm:mb-4" data-id="element-6">
            🎁 Cosa c'è dentro
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.05] mb-2 sm:mb-3" data-id="element-7">
            4 pilastri.
            <span className="text-primary" data-id="element-8"> Un solo Kit.</span>
          </h2>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-5" data-id="element-9">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          const accent = accentMap[benefit.accent];
          return <article key={i} className={`relative bg-bg-alt rounded-2xl border border-border-light p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 ${accent.border} ${benefit.featured ? 'shadow-[0_0_40px_rgba(239,68,68,0.10)]' : 'shadow-xl'}`} data-id="element-10">
                {/* Featured ribbon */}
                {benefit.featured && <div className="absolute -top-3 right-5 bg-secondary text-bg-dark text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/20 shadow-md" data-id="element-11">
                    ⭐ Risultato chiave
                  </div>}

                {/* Header: icon + metric */}
                <div className="flex items-start justify-between mb-3 sm:mb-4" data-id="element-12">
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl ${accent.iconBg} border ${accent.iconBorder} flex items-center justify-center flex-shrink-0`} data-id="element-13">
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${accent.iconColor}`} strokeWidth={2.25} data-id="element-14" />
                  </div>
                  <div className="text-right" data-id="element-15">
                    <p className={`font-black text-2xl sm:text-3xl leading-none tracking-tighter ${accent.metric}`} data-id="element-16">
                      {benefit.metric}
                    </p>
                    <p className="text-text-sec text-[10px] sm:text-xs font-bold uppercase tracking-wider mt-0.5" data-id="element-17">
                      {benefit.metricLabel}
                    </p>
                  </div>
                </div>

                {/* Title + Description */}
                <h3 className="text-lg sm:text-xl font-black text-text-main leading-tight mb-1.5" data-id="element-18">
                  {benefit.title}
                </h3>
                <p className="text-text-sec text-[13px] sm:text-[15px] leading-snug mb-4" data-id="element-19">
                  {benefit.desc}
                </p>

                {/* Bullets */}
                <ul className="space-y-1.5 pt-3 border-t border-border-light" data-id="element-20">
                  {benefit.bullets.map((b, j) => <li key={j} className="flex items-start gap-2 text-text-main text-[13px] sm:text-sm font-medium leading-snug" data-id="element-21">
                      <span className={`flex items-center justify-center w-4 h-4 rounded-full ${accent.iconBg} border ${accent.iconBorder} flex-shrink-0 mt-0.5`} data-id="element-22">
                        <CheckIcon className={`w-2.5 h-2.5 ${accent.iconColor}`} strokeWidth={4} data-id="element-23" />
                      </span>
                      <span data-id="element-24">{b}</span>
                    </li>)}
                </ul>
              </article>;
        })}
        </div>

        {/* Bottom synthesis CTA — tight */}
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 bg-bg-alt border border-border-light rounded-2xl px-5 py-4 sm:py-5" data-id="element-25">
          <p className="text-text-main text-base sm:text-lg font-semibold text-center sm:text-left leading-snug" data-id="element-26">
            ⚡ Tutto nella tua casella in{' '}
            <span className="text-cta-green font-black" data-id="element-27">30 secondi</span>.
          </p>
          <a href="#pricing" style={{
          boxShadow: '0 0 20px rgba(57,255,20,0.55), 0 8px 24px rgba(57,255,20,0.35)'
        }} className="inline-flex items-center justify-center gap-2 font-black text-sm sm:text-base px-6 py-3 sm:px-7 sm:py-3.5 rounded-xl border-2 border-white/30 whitespace-nowrap uppercase tracking-tight transition-transform hover:scale-[1.03] active:scale-[0.98] bg-[#22C55E] text-[#FFFFFF] w-full sm:w-auto" data-id="element-28">
            Vedi l'offerta
            <ArrowRightIcon className="w-4 h-4" strokeWidth={3} data-id="element-29" />
          </a>
        </div>
      </div>
    </section>;
}